/**
 * 予測誤差評価の「対象SKU」入力ブロック。
 *
 * テキスト入力（少数件の貼り付け）と CSV ファイル（最大30万SKU）の 2 経路を持ち、
 * バックエンドが併用をエラーにするため画面側で排他にする。`PredictionErrorEvaluationForm`
 * から切り出しているのは、フォームを 1 ファイル 500 行の規約内に収めるため。
 */

import { AlertTriangle, Ban } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TargetSkuFileInput } from './TargetSkuFileInput';
import type { ParsedSkuLines } from './parseSkuLines';

/**
 * テキスト入力で指定できる件数の上限。これを超える件数は CSV アップロードへ誘導する。
 *
 * 値は BE（`backend/core/src/handlers/ds/prediction_error_evaluation_target_skus.rs` の
 * `TARGET_SKUS_TEXT_MAX_COUNT`）が正で、**BE と同期して変更すること**。
 * ここで止めるのは、超過を送信前に気付かせるための早期検出に過ぎない
 * （`web::Json` の actix 既定上限 2MiB に対して余裕を取った値）。
 */
export const TARGET_SKUS_TEXT_MAX_COUNT = 10_000;

/** テキスト入力の件数が上限を超えているか */
export function isTargetSkusTextOverLimit(parsed: ParsedSkuLines): boolean {
  return parsed.skus.length > TARGET_SKUS_TEXT_MAX_COUNT;
}

export interface TargetSkuSectionProps {
  /** テキスト入力の生の値 */
  text: string;
  onTextChange: (value: string) => void;
  /** `text` のパース結果。送信可否の判定で親も使うため親から渡す */
  parsed: ParsedSkuLines;
  /** 選択中の CSV ファイル。未選択なら null */
  file: File | null;
  onFileChange: (file: File | null) => void;
  /**
   * S3 へのアップロードが失敗したときのメッセージ。
   * Sonner の toast ではなく該当コントロールの近くにインラインで出す
   */
  uploadError: string | null;
  /** アップロード進捗（0-100）。アップロード中以外は null */
  uploadProgress: number | null;
  /**
   * S3 へのアップロード中は true。
   *
   * 送信処理は押した時点のファイルをクロージャに捕捉するため、アップロード中に選択を
   * 変えられると「取り消したのにそのファイルでジョブが起動する」状態になる。
   * そのため、この間は対象SKUの指定経路を一切動かせないようにする
   */
  isUploading: boolean;
}

export function TargetSkuSection({
  text,
  onTextChange,
  parsed,
  file,
  onFileChange,
  uploadError,
  uploadProgress,
  isUploading,
}: TargetSkuSectionProps): React.ReactElement {
  const hasText = text.trim() !== '';
  const overLimit = isTargetSkusTextOverLimit(parsed);
  const truncatedInvalidLines = parsed.invalidLineTotal - parsed.invalidLines.length;

  return (
    <div className="space-y-2">
      <Label htmlFor="target-skus">対象SKU（任意・1行に1つのUUID）</Label>
      <Textarea
        id="target-skus"
        rows={5}
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder="3fa85f64-5717-4562-b3fc-2c963f66afa6"
        disabled={file !== null || isUploading}
      />
      <p className="text-xs text-muted-foreground">
        まとめて貼り付けられます。未入力の場合は全SKUを対象とします。テキスト入力は
        {TARGET_SKUS_TEXT_MAX_COUNT.toLocaleString('ja-JP')}件までで、これを超える場合は下のCSV
        ファイルを使ってください。
      </p>
      {file !== null && (
        <p className="text-xs text-muted-foreground">
          CSVファイルを指定しているため、テキスト入力は使えません（どちらか一方で指定してください）
        </p>
      )}
      {parsed.skus.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {parsed.skus.length.toLocaleString('ja-JP')}件のSKUを対象にします
        </p>
      )}
      {overLimit && (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            対象SKUが{parsed.skus.length.toLocaleString('ja-JP')}件で、テキスト入力の上限
            {TARGET_SKUS_TEXT_MAX_COUNT.toLocaleString('ja-JP')}
            件を超えています。CSVファイルのアップロードを使ってください
          </span>
        </p>
      )}
      {parsed.invalidLines.length > 0 && (
        <div className="space-y-1.5">
          {parsed.invalidLines.map((line) => (
            <p
              key={line.lineNumber}
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {line.lineNumber}行目「{line.value}」はUUIDとして読めません
              </span>
            </p>
          ))}
          {truncatedInvalidLines > 0 && (
            <p className="text-xs text-red-700">
              ほか{truncatedInvalidLines.toLocaleString('ja-JP')}件、読めない行があります
            </p>
          )}
        </div>
      )}
      {parsed.duplicateCount > 0 && (
        <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>同じSKUが指定されていたため、重複{parsed.duplicateCount}件を除きました</span>
        </p>
      )}

      <TargetSkuFileInput
        file={file}
        onFileChange={onFileChange}
        disabled={hasText || isUploading}
        disabledReason={
          isUploading
            ? 'アップロード中は対象SKUファイルを変更できません'
            : 'テキスト入力に対象SKUが入っているため、ファイルは選べません（どちらか一方で指定してください）'
        }
      />

      {uploadProgress !== null && (
        <p className="text-xs text-muted-foreground">
          対象SKUファイルをアップロードしています（{Math.floor(uploadProgress)}%）
        </p>
      )}
      {uploadError !== null && (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{uploadError}</span>
        </p>
      )}
    </div>
  );
}
